import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { formatUnicorn } from "../../../../shared/utils";
import { useQuery } from '@tanstack/react-query';
import { useCurrentProjectIdContext, useTranslationContext } from "./project-builder-main.jsx";
import { useEditProject } from "../../../components/project-builder-custom-hooks";
import { getProject } from '../../../apis/project-builder';
import EditProjectModal from './edit-project-modal.jsx';
import HelpOutlineOutlinedIcon from '@mui/icons-material/HelpOutlineOutlined';
import Tooltip from '@mui/material/Tooltip';
import LoaderSection from '../../../../shared/react/components/loader-section.jsx';
import SendQuoteConfirmationModal from './send-quote-confirmation-modal.jsx';
import DownloadsButtons from './downloads-buttons.jsx';
//import SidebarDiscount from './sidebar-discount.jsx';
import ProjectDiscountModal from './project-discount-modal.jsx';
import { formatCurrency } from './utilities.jsx';


export default function Sidebar({ review, received }) {
    const { translations } = useTranslationContext();
    const navigate = useNavigate();

    const { currentProjectId } = useCurrentProjectIdContext();

    const { data, isSuccess, isLoading, isError, error } = useQuery(['project-detail', currentProjectId], () => getProject(currentProjectId));

    const { mutate: editProject, isLoading: editIsLoading } = useEditProject(currentProjectId);

    //const [projectDiscount, setProjectDiscount] = useState(data.DiscountValue);

    const [salesTax, setSalesTax] = useState(data.salesTax);
    const [freight, setFreight] = useState(data.freight);

    const [width, setWidth] = useState(window.innerWidth);

    const handleWindowSizeChange = () => {
        setWidth(window.innerWidth);
    }
    useEffect(() => {
        window.addEventListener('resize', handleWindowSizeChange);
        return () => {
            window.removeEventListener('resize', handleWindowSizeChange);
        }
    }, []);

    const isMobile = width <= 768;

    const [isCollapsed, setIsCollapsed] = useState(isMobile);

    const handleToggleCollapse = () => {
        setIsCollapsed(prev => !prev);
    }

    const handleReviewQuote = () => {
        navigate(`/project-builder/${currentProjectId}/review-quote`);
    }

    const handleEditClick = () => {
        navigate(`/project-builder/${currentProjectId}`);
    }

    const netDealerPrice = () => {
        return data.totalDealerPrice + salesTax + freight;
    }

    const handleTaxFreightChange = () => {
        const name = event.target.name;
        const value = parseFloat(event.target.value);

        let currentSalesTax = salesTax;
        let currentFreight = freight;

        if (name === "SalesTax") {
            if(currentSalesTax === value) return;
            currentSalesTax = value;
            setSalesTax(value);
        } else {
            if(currentFreight === value) return;
            currentFreight = value;
            setFreight(value);
        }
                
        name === "SalesTax" ? setSalesTax(value) : setFreight(value);
        
        const payload = {
            id: currentProjectId,
            [name]: value,
        }

        editProject(payload);
    }

    let totalAccessoryProductCount = 0;

    data.Products?.map((product) => {
        totalAccessoryProductCount += product.accessories.length
    });

    let totalProductCount = data.products?.length + totalAccessoryProductCount;

    //sticky sidebar
    //didn't use position: sticky because parent #outer-container has overflow property which breaks position: sticky
    const [sidebarWidth, setSidebarWidth] = useState(undefined);
    const [sidebarTop, setSidebarTop] = useState(undefined);
    //const [containerBottom, setContainerBottom] = useState(undefined);

    const container = document.querySelector('.sidebar-wrapper');

    useEffect(() => {
        const sidebarEl = document.querySelector('.sidebar').getBoundingClientRect();
        //setContainerBottom(container.offsetHeight);
        setSidebarWidth(sidebarEl.width);
        setSidebarTop(sidebarEl.top);
    }, []);

    useEffect(() => {
        if (!sidebarTop) return;

        window.addEventListener('scroll', isSticky);
        return () => {
            window.removeEventListener('scroll', isSticky);
        };
    }, [sidebarTop]);

    const isSticky = (e) => {
        const sidebarEl = document.querySelector('.sidebar');
        const scrollTop = window.scrollY;
        if (scrollTop >= sidebarTop - 50) {
            sidebarEl.classList.remove('is-bottom');
            sidebarEl.classList.add('is-sticky');

            if (scrollTop >= container.offsetHeight - (review || received ? 500 : 0)) {
                sidebarEl.classList.remove('is-sticky');
                sidebarEl.classList.add('is-bottom');
            }
        } else {
            sidebarEl.classList.remove('is-sticky');
            sidebarEl.classList.remove('is-bottom');
        }
    };

    return (
        <div className="col-lg-4 sidebar-wrapper">
            {isLoading
                ? <LoaderSection show={true} />
                : <div className={`sidebar rounded-border ${isCollapsed ? 'collapsed' : ''}`} style={{ width: sidebarWidth }}>
                    <div className="sidebar-container">
                        <div className="collapse-toggle" onClick={handleToggleCollapse}></div>
                        {(!review && !received) &&
                            <>
                                <button className="btn btn-block no-arrow" type="button" onClick={handleReviewQuote}>{data.isMarketingProject ? translations.ReviewQuote : translations.ReviewQuote}</button>
                                <hr className="extra-margin" />
                                <div className="quote-details">
                                    <div className="section-title section-title--small section-title--flex">
                                        {data.name}
                                        <EditProjectModal
                                            openButtonText=""
                                            buttonClass="link-btn mr-0 ml-auto" />
                                    </div>
                                    <ul>
                                        <li>{translations.SendingTo}: <span>{data.accountName}</span></li>
                                        <li>{translations.Products}: <span>({totalProductCount})</span></li>
                                        <li>{translations.Attachments}: <span>({data.attachements?.length ? data.attachements.length : 0})</span></li>
                                        <li>{translations.StatusTxt}: <span>{data.status}</span></li>
                                        <li>{translations.ProjectFormat}: <span>{data.isMarketingProject ? translations.SalesProject : translations.QuoteProject}</span></li>
                                        <li>{translations.ProjectId}: <span>{data.id}</span></li>
                                    </ul>
                                </div>
                                {data.isMarketingProject
                                    ? null
                                    : <>
                                        <hr />

                                        <div className="quote-total-price">
                                            <span className="quote-total-price-label">
                                                <span style={{ textTransform: 'uppercase' }}>{translations.QuotePrice}:</span>
                                                <Tooltip title={translations.TaxFreightMessage} placement="top">
                                                    <HelpOutlineOutlinedIcon />
                                                </Tooltip>
                                            </span>
                                            <span>{formatCurrency(netDealerPrice(), 'es-ES')}</span>
                                        </div>
                                    </>
                                }
                            </>
                        }



                        {review &&
                            <>
                                <div className="buttons-container">
                                    <button className="btn btn-outline-alt mr-auto" type="button" onClick={handleEditClick}>{data.isMarketingProject ? formatUnicorn(translations.BackTo, translations.Project) : formatUnicorn(translations.BackTo, translations.Quote)}</button>
                                    <SendQuoteConfirmationModal currentProjectId={currentProjectId} />
                                </div>
                                <DownloadsButtons guid={data.guid} projectName={data.name} projectId={data.id} isMarketingProject={data.isMarketingProject} />
                                <hr className="extra-margin" />
                                <div className="quote-details">
                                    <div className="section-title section-title--small">{data.isMarketingProject ? "" : translations.Quote} {translations.Summary}</div>
                                    <ul>
                                        <li>{translations.ProjectType}: <span>{data.type}</span></li>
                                        <li>{translations.ProjectId}: <span>{data.id}</span></li>
                                        <li>{translations.CreatedBy}: <span>{data.createdBy}</span></li>
                                        <li>{translations.CreatedBy} {translations.Email}: <span>{data.createdByEmail}</span></li>
                                        <li>{translations.StatusTxt}: <span>{data.status}</span></li>
                                        <li>{translations.Format}: <span>{data.isMarketingProject ? translations.SalesProject : translations.QuoteProject}</span></li>
                                    </ul>
                                </div>
                                {data.isMarketingProject
                                    ? null
                                    : <>
                                        <hr />
                                        <div className="quote-totals">
                                            <ul>
                                                <li>{translations.Products}: ({totalProductCount}) <span>{formatCurrency(data.totalDealerPrice, 'es-ES')}</span></li>

                                                <li>{translations.Attachments}: ({data.attachements.length}) <span>0.00 €</span></li>
                                                <li>
                                                    {translations.SalesTax}:

                                                    <span className="sales-tax-input-container">
                                                        <input
                                                            type="text"
                                                            name="SalesTax"
                                                            style={{ textAlign: 'center' }}
                                                            //pattern="\d*"
                                                            maxlength="8"
                                                            value={salesTax}
                                                            onBlur={handleTaxFreightChange} />
                                                        &nbsp; €
                                                    </span>
                                                </li>
                                                <li>
                                                    {translations.Freight}:
                                                    <span className="freight-input-container">
                                                        <input
                                                            type="text"
                                                            name="Freight"
                                                            style={{ textAlign: 'center' }}
                                                            //pattern="\d*"
                                                            maxlength="8"
                                                            value={freight}
                                                            onBlur={handleTaxFreightChange} />
                                                        &nbsp; €
                                                    </span>
                                                </li>
                                            </ul>
                                        </div>
                                        <hr />
                                        <div className="quote-total-price">
                                            <span> {translations.QuotePrice.toUpperCase()}: </span>
                                            <span>{formatCurrency(netDealerPrice(), 'es-ES')}</span>
                                        </div>
                                    </>
                                }
                            </>
                        }

                        {received &&
                            <>
                                <div className="quote-details">
                                    <div className="section-title section-title--small">{data.isMarketingProject ? "" : translations.Quote} {translations.Summary}</div>
                                    <ul id="pdf-project-info">
                                        <li>{translations.ProjectType}: <span>{data.type}</span></li>
                                        <li>{translations.ProjectId}: <span>{data.id}</span></li>
                                        <li>{translations.CreatedBy}: <span>{data.createdBy}</span></li>
                                        <li>{translations.CreatedBy} {translations.Email}: <span>{data.createdByEmail}</span></li>
                                        <li>{translations.StatusTxt}: <span>{data.status}</span></li>
                                    </ul>
                                </div>
                                {data.isMarketingProject
                                    ? null
                                    : <>

                                        <hr />
                                        <div className="quote-totals">
                                            <ul>
                                            <li>{translations.Products}: ({totalProductCount}) <span>{formatCurrency(data.totalDealerPrice, 'es-ES')}</span></li>

                                            <li id="pdf-attach">{translations.Attachements}: ({data.attachements.length}) <span>0.00 €</span></li>
                                                <li id="pdf-sales">{translations.SalesTax}: <span>${data.salesTax}</span></li>
                                                <li id="pdf-freight">{translations.Freight}: <span>${data.freight}</span></li>
                                            </ul>
                                        </div>
                                        <hr />
                                        <div className="quote-total-price">
                                        <span> {translations.QuotePrice.toUpperCase()}: </span>
                                            <span>{formatCurrency(data.totalDealerPrice + salesTax + freight, 'es-ES')}</span>
                                        </div>
                                    </>
                                }
                            </>
                        }
                    </div>
                </div>
            }
        </div>
    );
}